import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import { getFirebaseDb } from './firebase/client';
import { deleteStoragePrefix } from './firebase/browserMedia';
import { ensureLevelDesignImagePathStored } from './firebase/levelDesignMedia';
import { deleteLevelDesignImageAssets } from './levelDesignImageAssets';
import { resolveCompactUploadedImageLayer } from './levelDesignsCompact';
import {
  UPLOADED_IMAGE_SHEET_ID,
  UPLOADED_IMAGE_TILESET_ID,
  toLevelDesignDocument,
} from './levelDesignsDeserialize';
import { TILE_SIZE } from '../game/worldConstants';
import { normalizeWorldAnnotations } from '../lib/worldAnnotations';
import { stripRuntimeStateFromAnnotations } from '../lib/worldAnnotationRuntime';
import type {
  LayerCell,
  LevelDesignDocument,
  LevelDesignSavePayload,
  TileReference,
  UploadedImageTransformSnapshot,
} from '../types/levelEditor';
import { isColorCell } from '../stores/editorStoreHelpers';

const COLLECTION_NAME = 'level_designs';

export const listLevelDesigns = async (userId: string): Promise<LevelDesignDocument[]> => {
  const db = getFirebaseDb();
  const designsQuery = query(
    collection(db, COLLECTION_NAME),
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc'),
  );
  const snapshot = await getDocs(designsQuery);
  return Promise.all(
    snapshot.docs.map((item) =>
      toLevelDesignDocument(item.id, item.data() as Record<string, unknown>),
    ),
  );
};

export const getLevelDesign = async (designId: string): Promise<LevelDesignDocument | null> => {
  const db = getFirebaseDb();
  const snapshot = await getDoc(doc(db, COLLECTION_NAME, designId));
  if (!snapshot.exists()) {
    return null;
  }
  return toLevelDesignDocument(snapshot.id, snapshot.data() as Record<string, unknown>);
};

const isUploadedImageTileReference = (cell: LayerCell): cell is TileReference =>
  !isColorCell(cell) &&
  cell.tilesetId === UPLOADED_IMAGE_TILESET_ID &&
  cell.sheetId === UPLOADED_IMAGE_SHEET_ID;

export const saveLevelDesign = async (
  userId: string,
  payload: LevelDesignSavePayload,
  designId?: string | null,
): Promise<string> => {
  const db = getFirebaseDb();
  const targetRef = designId
    ? doc(db, COLLECTION_NAME, designId)
    : doc(collection(db, COLLECTION_NAME));
  let uploadedImageAssetCounter = 0;
  const storedPathsBySourcePath = new Map<string, string>();

  interface ImagePathReference {
    layerId: string;
    set: (nextPath: string) => void;
  }

  const imagePathReferencesByPath = new Map<string, ImagePathReference[]>();

  const registerImagePathReference = (
    layerId: string,
    imagePath: string,
    set: (nextPath: string) => void,
  ): void => {
    if (!imagePath.trim()) {
      return;
    }
    const references = imagePathReferencesByPath.get(imagePath) ?? [];
    references.push({ layerId, set });
    imagePathReferencesByPath.set(imagePath, references);
  };

  const storeUploadedImagePath = async (layerId: string, imagePath: string): Promise<string> => {
    const existing = storedPathsBySourcePath.get(imagePath);
    if (existing) {
      return existing;
    }

    uploadedImageAssetCounter += 1;
    const storedPath = await ensureLevelDesignImagePathStored({
      assetId: `${layerId}-${uploadedImageAssetCounter}`,
      designId: targetRef.id,
      imagePath,
      userId,
    });
    storedPathsBySourcePath.set(imagePath, storedPath);
    return storedPath;
  };

  const isNewDocument = !designId;
  const layers = payload.layers.map((layer) => {
    const compactUploadedImage = resolveCompactUploadedImageLayer(layer, TILE_SIZE);
    const normalizedTiles = Object.fromEntries(
      Object.entries(layer.tiles).map(([key, tile]) => [key, { ...tile }]),
    );
    const layerRecord: Record<string, unknown> = {
      id: layer.id,
      name: layer.name,
      ...(layer.layerType === 'animated-gif' ? { layerType: 'animated-gif' as const } : {}),
      visible: layer.visible,
      locked: layer.locked,
      opacity: layer.opacity,
      traversalDefault: layer.traversalDefault,
      ...(layer.renderPlane === 'foreground' ? { renderPlane: 'foreground' as const } : {}),
      tiles: compactUploadedImage ? {} : normalizedTiles,
      ...(compactUploadedImage
        ? {
            uploadedImageTransform: {
              ...compactUploadedImage,
            },
          }
        : {}),
    };

    if (compactUploadedImage) {
      registerImagePathReference(layer.id, compactUploadedImage.imagePath, (nextPath) => {
        const transform = layerRecord.uploadedImageTransform as UploadedImageTransformSnapshot;
        transform.imagePath = nextPath;
      });
      return layerRecord;
    }

    Object.values(normalizedTiles).forEach((cell) => {
      if (!isUploadedImageTileReference(cell)) {
        return;
      }
      registerImagePathReference(layer.id, cell.imagePath, (nextPath) => {
        cell.imagePath = nextPath;
      });
    });
    return layerRecord;
  });

  for (const [imagePath, references] of imagePathReferencesByPath) {
    const storedPath = await storeUploadedImagePath(references[0].layerId, imagePath);
    references.forEach((reference) => {
      reference.set(storedPath);
    });
  }
  const occlusionMaskUrlTrimmed = payload.occlusionMaskUrl?.trim();
  const storedOcclusionMaskUrl = occlusionMaskUrlTrimmed
    ? await ensureLevelDesignImagePathStored({
        assetId: 'occlusion-mask',
        designId: targetRef.id,
        imagePath: occlusionMaskUrlTrimmed,
        userId,
      })
    : null;

  const data: Record<string, unknown> = {
    userId,
    name: payload.name.slice(0, 40) || 'Untitled Design',
    tilesetPack: payload.tilesetPack,
    canvasWidth: payload.canvasWidth,
    canvasHeight: payload.canvasHeight,
    layers,
    occlusionMaskUrl: storedOcclusionMaskUrl,
    traversalOverrides: payload.traversalOverrides,
    spawnPoint: payload.spawnPoint,
    annotations: stripRuntimeStateFromAnnotations(
      normalizeWorldAnnotations(payload.annotations, {
        tileWidth: payload.canvasWidth,
        tileHeight: payload.canvasHeight,
      }),
    ),
    mediaStorageMigratedAt: serverTimestamp(),
    mediaStorageMigrationSource: 'save',
    mediaStorageVersion: 'storage-v1',
    updatedAt: serverTimestamp(),
  };
  if (isNewDocument) {
    data.createdAt = serverTimestamp();
  }

  await setDoc(targetRef, data, { merge: true });
  const animatedGifLayerCount = payload.layers.filter(
    (layer) => layer.layerType === 'animated-gif',
  ).length;
  console.info('[level-designs] Saved level design', {
    animatedGifLayerCount,
    designId: targetRef.id,
    layerCount: payload.layers.length,
    name: data.name,
    userId,
  });

  return targetRef.id;
};

export const replaceOwnedLevelDesignAnnotations = async (
  userId: string,
  designId: string,
  annotations: LevelDesignDocument['annotations'],
): Promise<LevelDesignDocument> => {
  const normalizedDesignId = designId.trim();
  if (!normalizedDesignId) {
    throw new Error('Level design not found.');
  }

  const db = getFirebaseDb();
  const designRef = doc(db, COLLECTION_NAME, normalizedDesignId);
  const snapshot = await getDoc(designRef);
  if (!snapshot.exists()) {
    throw new Error('Level design not found.');
  }

  const current = snapshot.data() as Record<string, unknown>;
  if (String(current.userId ?? '').trim() !== userId) {
    throw new Error('Level design is not editable by current user.');
  }

  const canvasWidth = Math.max(1, Math.floor(Number(current.canvasWidth ?? 64) || 64));
  const canvasHeight = Math.max(1, Math.floor(Number(current.canvasHeight ?? 64) || 64));
  const normalizedAnnotations = stripRuntimeStateFromAnnotations(
    normalizeWorldAnnotations(annotations ?? [], {
      tileWidth: canvasWidth,
      tileHeight: canvasHeight,
    }),
  );

  await setDoc(
    designRef,
    {
      annotations: normalizedAnnotations,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  const updated = await getLevelDesign(normalizedDesignId);
  if (!updated) {
    throw new Error('Level design annotations were saved but could not be read back.');
  }
  return updated;
};

export const deleteLevelDesign = async (designId: string): Promise<void> => {
  const db = getFirebaseDb();
  const snapshot = await getDoc(doc(db, COLLECTION_NAME, designId)).catch(() => null);
  const userId = snapshot?.exists() ? String(snapshot.data()?.userId ?? '').trim() : '';

  if (userId) {
    await deleteStoragePrefix(`users/${userId}/level-designs/${designId}`);
  }

  await deleteDoc(doc(db, COLLECTION_NAME, designId));
  deleteLevelDesignImageAssets(designId);
};
